/**
 * Mailbox read hooks — folders, threads, messages, single message, and the
 * inline "sync now" trigger. All read from the Vault-backed routes
 * (/arches/email/folders|threads|messages and POST /connections/:id/sync).
 */

import {
  useQuery,
  useQueries,
  useMutation,
  useQueryClient,
  keepPreviousData,
  type QueryClient,
} from '@tanstack/react-query';
import { useState, useCallback } from 'react';
import { api } from '@/lib/api';
import { consumeSSEFrames } from '@/lib/sse-parser';
import { toast } from '@/components/ui/use-toast';
import { queryKeys } from '@/lib/query-keys';
import type {
  EmailFolderDto,
  EmailThreadDto,
  EmailCategoryDto,
  EmailLabelMailboxDto,
  EmailMessageDto,
  EmailMessageDetailDto,
  EmailMailboxAttachmentDto,
  EmailSearchField,
  AdvancedSearchGroup,
  EmailSenderGroupDto,
  EmailCategoryGroupDto,
  EmailBulkFilterScope,
  EmailAdvancedFilters,
} from '@/lib/api';

const mb = queryKeys.email.mailbox;
const conn = queryKeys.email.connections;

export function useEmailFolders(accountId: string | undefined) {
  return useQuery<EmailFolderDto[]>({
    queryKey: mb.folders(accountId ?? ''),
    queryFn: async () => (await api.listEmailMailboxFolders(accountId as string)).folders,
    enabled: !!accountId,
  });
}

/**
 * Folders for many accounts at once — one query per account, keyed identically
 * to `useEmailFolders` so the single-account view and the unified "All accounts"
 * sidebar share a cache entry per account. Returns a `Map<accountId, folders>`
 * plus an aggregate loading flag. Pass `[]` to fetch nothing (the non-unified
 * case), which keeps this a single, unconditional hook call.
 */
export function useEmailFoldersByAccount(accountIds: string[]) {
  return useQueries({
    queries: accountIds.map((accountId) => ({
      queryKey: mb.folders(accountId),
      queryFn: async () => (await api.listEmailMailboxFolders(accountId)).folders,
      enabled: !!accountId,
    })),
    combine: (results) => ({
      foldersByAccount: new Map<string, EmailFolderDto[]>(
        accountIds.map((id, i) => [id, results[i]?.data ?? []]),
      ),
      isLoading: results.some((r) => r.isLoading),
    }),
  });
}

export function useEmailThreads(accountId: string | undefined, folderId?: string) {
  return useQuery<EmailThreadDto[]>({
    queryKey: mb.threadList(accountId ?? '', folderId),
    queryFn: async () => (await api.listEmailMailboxThreads(accountId as string, folderId)).threads,
    enabled: !!accountId,
  });
}

/** The holder's category set (cross-account). Cached; used by filters + grouping.
 *  `refetchOnMount: 'always'` so opening Manage Categories re-pulls the server set
 *  in the background even within the stale window — otherwise an empty result
 *  cached before the defaults were seeded (first open on a fresh mailbox, or
 *  before the server picked up the seed) would keep showing "No categories yet". */
export function useEmailCategories(enabled = true) {
  return useQuery<EmailCategoryDto[]>({
    queryKey: [...mb.all, 'categories'],
    queryFn: async () => (await api.listEmailMailboxCategories()).categories,
    staleTime: 5 * 60 * 1000,
    refetchOnMount: 'always',
    enabled,
  });
}

// Category management (create/rename/recolor/delete). Each refreshes the
// category list; rename/recolor/delete also refresh the message aggregates
// (the By Category cards + drill-down) since the displayed name/colour or a
// thread's membership changed. Delete sets member threads to Uncategorized
// (ON DELETE SET NULL server-side). Surfaces the server's typed error message.
const categoryErr = (e: unknown) =>
  e instanceof Error && e.message ? e.message : 'Category operation failed';
const invalidateCategories = (qc: QueryClient) => {
  qc.invalidateQueries({ queryKey: [...mb.all, 'categories'] });
  qc.invalidateQueries({ queryKey: mb.messages() });
};

/** Create a category (rejects a duplicate name server-side). */
export function useCreateEmailCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; color?: string; sortOrder?: number }) =>
      api.createEmailMailboxCategory(input),
    onSuccess: () => {
      toast({ description: 'Category created' });
      invalidateCategories(qc);
    },
    onError: (e) => toast({ description: categoryErr(e), variant: 'destructive' }),
  });
}

/** Rename / recolor / reorder a category. */
export function useUpdateEmailCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      categoryId,
      ...input
    }: {
      categoryId: string;
      name?: string;
      color?: string;
      sortOrder?: number;
    }) => api.updateEmailMailboxCategory(categoryId, input),
    onSuccess: () => invalidateCategories(qc),
    onError: (e) => toast({ description: categoryErr(e), variant: 'destructive' }),
  });
}

/** Delete a category; member threads become Uncategorized. */
export function useDeleteEmailCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (categoryId: string) => api.deleteEmailMailboxCategory(categoryId),
    onSuccess: () => {
      toast({ description: 'Category deleted' });
      invalidateCategories(qc);
    },
    onError: (e) => toast({ description: categoryErr(e), variant: 'destructive' }),
  });
}

// ── Labels (account-scoped, many-per-message) ──────────────────────────────
// The account's editable label set + the per-message apply. Label CRUD refreshes
// the label list and the message lists (row chips carry the name/colour); apply
// patches the affected message's chips from the server response.

/** The account's label set (chips + management dialog + filter). Cached 5 min. */
export function useEmailLabels(accountId: string | undefined, enabled = true) {
  return useQuery<EmailLabelMailboxDto[]>({
    queryKey: [...mb.all, 'labels', accountId ?? ''],
    queryFn: async () => (await api.listEmailMailboxLabels(accountId as string)).labels,
    staleTime: 5 * 60 * 1000,
    enabled: enabled && !!accountId,
  });
}

const labelErr = (e: unknown) =>
  e instanceof Error && e.message ? e.message : 'Label operation failed';
const invalidateLabels = (qc: QueryClient, accountId: string) => {
  qc.invalidateQueries({ queryKey: [...mb.all, 'labels', accountId] });
  qc.invalidateQueries({ queryKey: mb.messages() });
};

/** Create a label (rejects a duplicate name per account server-side). */
export function useCreateEmailLabel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { accountId: string; name: string; color?: string; sortOrder?: number }) =>
      api.createEmailMailboxLabel(input),
    onSuccess: (_d, v) => {
      toast({ description: 'Label created' });
      invalidateLabels(qc, v.accountId);
    },
    onError: (e) => toast({ description: labelErr(e), variant: 'destructive' }),
  });
}

/** Rename / recolor / reorder a label. */
export function useUpdateEmailLabel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      labelId,
      ...input
    }: {
      labelId: string;
      accountId: string;
      name?: string;
      color?: string;
      sortOrder?: number;
    }) => api.updateEmailMailboxLabel(labelId, input),
    onSuccess: (_d, v) => invalidateLabels(qc, v.accountId),
    onError: (e) => toast({ description: labelErr(e), variant: 'destructive' }),
  });
}

/** Delete a label; join rows cascade (chips vanish from any message that had it). */
export function useDeleteEmailLabel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ labelId, accountId }: { labelId: string; accountId: string }) =>
      api.deleteEmailMailboxLabel(labelId, accountId),
    onSuccess: (_d, v) => {
      toast({ description: 'Label deleted' });
      invalidateLabels(qc, v.accountId);
    },
    onError: (e) => toast({ description: labelErr(e), variant: 'destructive' }),
  });
}

/** Replace a message's label set; patches the row + reading-pane chips from the
 *  server response, then reconciles the message lists. */
export function useApplyMessageLabels() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      messageId,
      accountId,
      labelIds,
    }: {
      messageId: string;
      accountId: string;
      labelIds: string[];
    }) => api.applyEmailMailboxMessageLabels(messageId, accountId, labelIds),
    onSuccess: (res, v) => {
      applyToMessageLists(qc, v.messageId, (m) => ({ ...m, labels: res.labels }));
      qc.setQueryData<EmailMessageDetailDto>(mb.message(v.messageId), (old) =>
        old ? { ...old, labels: res.labels } : old,
      );
      qc.invalidateQueries({ queryKey: mb.messages() });
    },
    onError: (e) => toast({ description: labelErr(e), variant: 'destructive' }),
  });
}

/** RFC 8058 one-click unsubscribe (server-side POST). The mailto/URL fallback is
 *  opened directly by the caller — this hook is only the one-click path. */
export function useUnsubscribeMessage() {
  return useMutation({
    mutationFn: (messageId: string) => api.unsubscribeEmailMailboxMessage(messageId),
    onSuccess: () => toast({ description: 'Unsubscribe request sent' }),
    onError: (e) =>
      toast({
        description: e instanceof Error && e.message ? e.message : 'Unsubscribe failed',
        variant: 'destructive',
      }),
  });
}

/** Add a `.ics` invite attachment to the Vault calendar (idempotent on ical_uid).
 *  Reports how many events were added vs already-present. */
export function useAddCalendarInvite() {
  return useMutation({
    mutationFn: ({ messageId, attachmentId }: { messageId: string; attachmentId: string }) =>
      api.addEmailCalendarInvite(messageId, attachmentId),
    onSuccess: (res) => {
      const msg =
        res.added > 0
          ? `Added ${res.added} event${res.added === 1 ? '' : 's'} to your calendar`
          : 'Already on your calendar';
      toast({ description: msg });
    },
    onError: (e) =>
      toast({
        description: e instanceof Error && e.message ? e.message : 'Could not add to calendar',
        variant: 'destructive',
      }),
  });
}

/** Uncategorized-thread count in the given scope — the AI-cost estimate shown
 *  before a bulk categorize (one call per thread). Fetched only when enabled. */
export function useEmailUncategorizedCount(
  scope: {
    accountId?: string;
    folderId?: string;
    folderSystemRole?: 'inbox' | 'sent' | 'drafts' | 'trash' | 'spam' | 'archive';
  },
  enabled = true,
) {
  return useQuery<number>({
    queryKey: [
      ...mb.messages(),
      'uncategorized-count',
      scope.accountId ?? 'unified',
      scope.folderId ?? scope.folderSystemRole ?? 'all',
    ],
    queryFn: async () => (await api.getEmailMailboxUncategorizedCount(scope)).count,
    enabled,
  });
}

/** Per-account opt-in for AI auto-categorize of new inbox mail (modules flag). */
export function useSetAccountAutoCategorize() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ accountId, enabled }: { accountId: string; enabled: boolean }) =>
      api.setEmailMailboxFeatures(accountId, { autoCategorizeEnabled: enabled }),
    onSuccess: () => qc.invalidateQueries({ queryKey: conn.all }),
    onError: (e) => toast({ description: categoryErr(e), variant: 'destructive' }),
  });
}

/**
 * Bulk-categorize the uncategorized backlog (one AI call per thread). Invalidates
 * the message aggregates so the By-Category cards + drill-down reflect the fills.
 */
export function useBulkCategorize() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Parameters<typeof api.bulkCategorizeEmailMailbox>[0]) =>
      api.bulkCategorizeEmailMailbox(input),
    onSuccess: (res) => {
      toast({
        description:
          res.categorized > 0
            ? `Categorized ${res.categorized} conversation${res.categorized === 1 ? '' : 's'}${
                res.remaining > 0 ? ` · ${res.remaining} left` : ''
              }`
            : 'Nothing to categorize',
      });
      qc.invalidateQueries({ queryKey: mb.messages() });
    },
    onError: (e) => toast({ description: categoryErr(e), variant: 'destructive' }),
  });
}

// The backend caps a single list request at 1000 rows. To honor a larger
// `limit` (the "Load all" path) without a 400 — which used to blank the whole
// list — page through with offset and concatenate. The result stays a flat
// EmailMessageDto[], so the optimistic updaters below are unaffected.
const FETCH_PAGE = 1000;

type ListMessagesOpts = NonNullable<Parameters<typeof api.listEmailMailboxMessages>[1]>;

async function fetchMessagesPaged(
  accountId: string | undefined,
  opts: ListMessagesOpts,
): Promise<EmailMessageDto[]> {
  const target = opts.limit ?? 50;
  if (target <= FETCH_PAGE) {
    return (await api.listEmailMailboxMessages(accountId, opts)).messages;
  }
  const all: EmailMessageDto[] = [];
  for (let offset = 0; offset < target; offset += FETCH_PAGE) {
    const pageLimit = Math.min(FETCH_PAGE, target - offset);
    const page = (
      await api.listEmailMailboxMessages(accountId, { ...opts, limit: pageLimit, offset })
    ).messages;
    all.push(...page);
    if (page.length < pageLimit) break; // reached the end of the folder
  }
  return all;
}

export function useEmailMessages(
  accountId: string | undefined,
  opts: {
    folderId?: string;
    threadId?: string;
    /** Drill-down to one smart category ('uncategorized' = threads with none). */
    categoryId?: string;
    /** Drill-down to one label (message-level). */
    labelId?: string;
    screener?: boolean;
    isRead?: boolean;
    isStarred?: boolean;
    hasAttachments?: boolean;
    limit?: number;
  } & EmailAdvancedFilters = {},
) {
  return useQuery<EmailMessageDto[]>({
    queryKey: mb.messageList(
      accountId ?? '',
      opts.folderId,
      opts.threadId,
      opts.screener ?? false,
      opts.limit,
      {
        isRead: opts.isRead,
        isStarred: opts.isStarred,
        hasAttachments: opts.hasAttachments,
        categoryId: opts.categoryId,
        labelId: opts.labelId,
        answered: opts.answered,
        inAddressBook: opts.inAddressBook,
        receivedAfter: opts.receivedAfter,
        receivedBefore: opts.receivedBefore,
      },
    ),
    queryFn: async () => fetchMessagesPaged(accountId as string, opts),
    enabled: !!accountId,
    // "Load more" grows `limit`, which changes the query key. Keep showing the
    // current rows while the larger page loads so the (virtualized) list never
    // unmounts and jumps the scroll back to the top.
    placeholderData: keepPreviousData,
  });
}

/**
 * Unified inbox — merged messages across ALL of the holder's connected
 * accounts (no `accountId`). Keyed under the shared `mailbox.messages()` prefix
 * so the optimistic write actions (mark-read/star/move/delete) and
 * `reconcile()` patch it automatically. Pair with `folderSystemRole: 'inbox'`
 * so Sent/Drafts are excluded.
 */
export function useUnifiedEmailMessages(
  opts: {
    folderSystemRole?: 'inbox' | 'sent' | 'drafts' | 'trash' | 'spam' | 'archive';
    /** Drill-down to one smart category ('uncategorized' = threads with none). */
    categoryId?: string;
    /** Drill-down to one label (message-level). */
    labelId?: string;
    screener?: boolean;
    /** Accounts the (per-account) screener applies to. Mail from any other
     *  account is never screened in the merged view — otherwise one account's
     *  screener would hide another account's mail from All accounts. */
    screenerAccountIds?: string[];
    isRead?: boolean;
    isStarred?: boolean;
    hasAttachments?: boolean;
    limit?: number;
  } & EmailAdvancedFilters = {},
  enabled = true,
) {
  return useQuery<EmailMessageDto[]>({
    queryKey: mb.messageList(
      'unified',
      opts.folderSystemRole,
      undefined,
      opts.screener ?? false,
      opts.limit,
      {
        isRead: opts.isRead,
        isStarred: opts.isStarred,
        hasAttachments: opts.hasAttachments,
        categoryId: opts.categoryId,
        labelId: opts.labelId,
        screenerAccountIds: opts.screenerAccountIds,
        answered: opts.answered,
        inAddressBook: opts.inAddressBook,
        receivedAfter: opts.receivedAfter,
        receivedBefore: opts.receivedBefore,
      },
    ),
    queryFn: async () =>
      fetchMessagesPaged(undefined, {
        folderSystemRole: opts.folderSystemRole,
        categoryId: opts.categoryId,
        labelId: opts.labelId,
        screener: opts.screener,
        screenerAccountIds: opts.screenerAccountIds,
        isRead: opts.isRead,
        isStarred: opts.isStarred,
        hasAttachments: opts.hasAttachments,
        answered: opts.answered,
        inAddressBook: opts.inAddressBook,
        receivedAfter: opts.receivedAfter,
        receivedBefore: opts.receivedBefore,
        limit: opts.limit,
      }),
    enabled,
    // Keep the prior rows visible while a larger "load more" page loads (the
    // limit is in the query key) — avoids the virtualized list jumping to top.
    placeholderData: keepPreviousData,
  });
}

/**
 * Full-text + substring search over synced mail; enabled once the term is
 * non-empty. `fields` is a combinable subset of sender / recipient / subject /
 * body (empty = all); `folderId` restricts to a single folder ("this folder"
 * scope); `filters` apply the same quick filters as the list view.
 */
// Global search returns up to this many matches in one request (the server caps
// at 1000). Generous so an all-accounts/all-folders "search everything" isn't
// silently cut to a page.
const SEARCH_RESULT_LIMIT = 500;

export function useEmailSearch(
  query: string,
  accountId: string | undefined,
  fields: EmailSearchField[] = [],
  folderId?: string,
  filters?: {
    isRead?: boolean;
    isStarred?: boolean;
    hasAttachments?: boolean;
  } & EmailAdvancedFilters,
) {
  const trimmed = query.trim();
  return useQuery<EmailMessageDto[]>({
    queryKey: mb.search(accountId, trimmed, fields, folderId ?? 'all', filters),
    queryFn: async () =>
      (
        await api.searchEmailMailbox(
          trimmed,
          accountId,
          fields,
          folderId,
          filters,
          SEARCH_RESULT_LIMIT,
        )
      ).messages,
    enabled: trimmed.length >= 2,
  });
}

/** Nested AND/OR advanced search over synced mail. Runs once a query is set. */
export function useAdvancedEmailSearch(
  query: AdvancedSearchGroup | null,
  accountIds: string[] | undefined,
  folderId?: string,
) {
  return useQuery<EmailMessageDto[]>({
    queryKey: mb.advancedSearch(accountIds, folderId ?? 'all', query ?? {}),
    queryFn: async () =>
      (
        await api.advancedSearchEmailMailbox(query!, {
          accountIds,
          folderId,
          limit: SEARCH_RESULT_LIMIT,
        })
      ).messages,
    enabled: query !== null,
  });
}

/**
 * "By Sender" overview — groups the scoped folder view by sender address. The
 * `excludeStarred` / `excludeUnread` toggles narrow the groups AND their bulk-
 * action sets identically. Keyed under the `messages()` prefix so a bulk write's
 * reconcile refetches it. Unified mode passes `folderSystemRole: 'inbox'`.
 */
export function useEmailBySender(
  accountId: string | undefined,
  opts: {
    folderId?: string;
    folderSystemRole?: 'inbox' | 'sent' | 'drafts' | 'trash' | 'spam' | 'archive';
    screener?: boolean;
    excludeStarred?: boolean;
    excludeUnread?: boolean;
    excludeAttachments?: boolean;
  } & EmailAdvancedFilters,
  enabled = true,
) {
  return useQuery<EmailSenderGroupDto[]>({
    queryKey: mb.bySender(accountId ?? 'unified', opts.folderId ?? opts.folderSystemRole ?? 'all', {
      excludeStarred: opts.excludeStarred,
      excludeUnread: opts.excludeUnread,
      excludeAttachments: opts.excludeAttachments,
      answered: opts.answered,
      inAddressBook: opts.inAddressBook,
      receivedAfter: opts.receivedAfter,
      receivedBefore: opts.receivedBefore,
    }),
    queryFn: async () => (await api.listEmailMailboxBySender(accountId, opts)).groups,
    enabled,
  });
}

/**
 * "By Category" overview — groups the scoped folder view by the thread's smart
 * category (plus an Uncategorized group). Same scope + exclude semantics as
 * {@link useEmailBySender}; keyed under `messages()` so a bulk write's reconcile
 * refetches it. Unified mode passes `folderSystemRole: 'inbox'`.
 */
export function useEmailByCategory(
  accountId: string | undefined,
  opts: {
    folderId?: string;
    folderSystemRole?: 'inbox' | 'sent' | 'drafts' | 'trash' | 'spam' | 'archive';
    screener?: boolean;
    excludeStarred?: boolean;
    excludeUnread?: boolean;
    excludeAttachments?: boolean;
  } & EmailAdvancedFilters,
  enabled = true,
) {
  return useQuery<EmailCategoryGroupDto[]>({
    queryKey: mb.byCategory(
      accountId ?? 'unified',
      opts.folderId ?? opts.folderSystemRole ?? 'all',
      {
        excludeStarred: opts.excludeStarred,
        excludeUnread: opts.excludeUnread,
        excludeAttachments: opts.excludeAttachments,
        answered: opts.answered,
        inAddressBook: opts.inAddressBook,
        receivedAfter: opts.receivedAfter,
        receivedBefore: opts.receivedBefore,
      },
    ),
    queryFn: async () => (await api.listEmailMailboxByCategory(accountId, opts)).groups,
    enabled,
  });
}

// The dedicated Screener inbox shows only held (pending-sender) mail. Held mail
// is a small set, so one bounded fetch powers both the sidebar count badge and
// the view. Keyed under `messages()` so a decide (approve/reject) invalidation
// refetches it.
const SCREENER_LIMIT = 500;

/** Held (pending-sender) mail for ONE account's inbox — the Screener view/count. */
export function useScreenerMessages(
  accountId: string | undefined,
  folderId: string | undefined,
  enabled = true,
) {
  return useQuery<EmailMessageDto[]>({
    queryKey: mb.messageList(accountId ?? '', folderId, undefined, false, undefined, {
      screenerView: true,
    }),
    queryFn: async () =>
      fetchMessagesPaged(accountId, { folderId, screenerView: true, limit: SCREENER_LIMIT }),
    enabled: enabled && !!accountId && !!folderId,
    placeholderData: keepPreviousData,
  });
}

/** Held mail across ALL accounts' inboxes — the unified "All Screened" view/count. */
export function useUnifiedScreenerMessages(enabled = true) {
  return useQuery<EmailMessageDto[]>({
    queryKey: mb.messageList('unified', 'inbox', undefined, false, undefined, {
      screenerView: true,
    }),
    queryFn: async () =>
      fetchMessagesPaged(undefined, {
        folderSystemRole: 'inbox',
        screenerView: true,
        limit: SCREENER_LIMIT,
      }),
    enabled,
    placeholderData: keepPreviousData,
  });
}

/** Approve/reject a screened sender by address (inline, from the Screener view).
 *  Approve lets the sender into the inbox; reject trashes their mail. Refreshes
 *  the screener lists, the message lists, and folder counts. */
export function useDecideSenderByAddress() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      accountId,
      senderAddress,
      decision,
    }: {
      accountId: string;
      senderAddress: string;
      decision: 'approved' | 'rejected';
    }) => api.decideEmailMailboxScreenerBySender(accountId, senderAddress, decision),
    onSuccess: (res, v) => {
      qc.invalidateQueries({ queryKey: conn.all });
      qc.invalidateQueries({ queryKey: mb.messages() });
      qc.invalidateQueries({ queryKey: [...mb.all, 'folders'] });
      const label =
        v.decision === 'approved'
          ? 'Sender approved — their mail is now in your inbox'
          : res?.trashed && res.trashed > 0
            ? `Rejected — ${res.trashed} message${res.trashed === 1 ? '' : 's'} moved to Trash`
            : 'Sender rejected';
      toast({ description: label });
    },
    onError: (e) =>
      toast({
        description: e instanceof Error && e.message ? e.message : 'Screener action failed',
        variant: 'destructive',
      }),
  });
}

export function useEmailMessage(messageId: string | undefined) {
  return useQuery<EmailMessageDetailDto>({
    queryKey: mb.message(messageId ?? ''),
    queryFn: async () => api.getEmailMailboxMessage(messageId as string),
    enabled: !!messageId,
  });
}

/** A message's attachments (bytes live in the Vault; downloaded on demand). */
export function useMessageAttachments(messageId: string | undefined) {
  return useQuery<EmailMailboxAttachmentDto[]>({
    queryKey: mb.attachments(messageId ?? ''),
    queryFn: async () =>
      (await api.listEmailMailboxMessageAttachments(messageId as string)).attachments,
    enabled: !!messageId,
  });
}

/** Trigger a full-account pull-sync (every folder); refreshes folders + messages. */
export function useSyncAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (accountId: string) => api.syncEmailAccount(accountId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: mb.all });
      queryClient.invalidateQueries({ queryKey: conn.all });
    },
  });
}

export interface SyncProgress {
  folderIndex: number;
  folderTotal: number;
  folderPath: string;
  fetched: number;
  total: number;
}

/** "Inbox: 120 of 340 · folder 2 of 5" for the live sync readout. */
export function formatSyncProgress(p: SyncProgress): string {
  const leaf = p.folderPath.split(/[/.]/).filter(Boolean).pop() || p.folderPath;
  const count = p.total > 0 ? `${Math.min(p.fetched, p.total)} of ${p.total}` : `${p.fetched}`;
  const scope = p.folderTotal > 1 ? ` · folder ${p.folderIndex} of ${p.folderTotal}` : '';
  return `${leaf}: ${count}${scope}`;
}

/** Which account a multi-account "Sync all" is currently streaming. */
export interface SyncAccountScope {
  index: number;
  total: number;
  emailAddress: string;
}

/** One account's failure in a multi-account "Sync all" run. */
export interface SyncFailure {
  emailAddress: string;
  /** User-facing reason (server `CODE: hint` mapped via {@link friendlySyncError}). */
  message: string;
}

export interface SyncStreamController {
  /** True from start until the stream completes (or errors). */
  syncing: boolean;
  /** Latest per-folder progress, or null when idle / between folders. */
  progress: SyncProgress | null;
  /**
   * In a multi-account run (`startAll`), the account currently syncing so the
   * readout can show "account 2 of 3". Null for a single-account `start`.
   */
  accountScope: SyncAccountScope | null;
  /** Total messages upserted once the sync completes (summed across accounts). */
  result: { messagesUpserted: number } | null;
  /** Headline error if the sync failed (or a summary of per-account failures). */
  error: string | null;
  /**
   * Per-account failures from the last `startAll` (empty on success / single
   * `start`). The UI lists these under {@link error} so "Sync all" says *which*
   * accounts failed and *why*, not just a count.
   */
  failures: SyncFailure[];
  start: (accountId: string) => Promise<void>;
  /**
   * Sync every given account in sequence — the unified "Sync all". One account
   * failing doesn't abort the rest; failures are summarized in `error` +
   * `failures` while the successful accounts still count toward `result`.
   */
  startAll: (accounts: { accountId: string; emailAddress: string }[]) => Promise<void>;
}

/**
 * Turn a server sync error into a short user-facing reason. The stream sends
 * `CODE: hint` strings (see `runEmailAccountSync` / `buildImapAuthForAccount`);
 * map the known codes to plain language and otherwise strip the `CODE:` prefix
 * so the reader never sees a raw enum. Falls back to the message unchanged.
 */
export function friendlySyncError(raw: string): string {
  const code = raw.split(':', 1)[0];
  switch (code) {
    case 'TOKEN_EXPIRED':
      return 'Sign-in expired — reconnect this account.';
    case 'SYNC_ACCOUNT_NOT_FOUND':
      return 'Account not found or disconnected — reconnect it.';
    case 'GRAPH_SYNC_NOT_IMPLEMENTED':
      return 'Outlook / Microsoft sync isn’t available yet.';
    case 'POP3_SYNC_NOT_IMPLEMENTED':
      return 'POP3 sync isn’t available yet.';
    case 'SYNC_FOLDER_NOT_SYNCABLE':
      return 'Run a full sync for this account first.';
    default: {
      const stripped = raw.match(/^[A-Z][A-Z0-9_]+:\s*(.+)$/);
      return stripped ? stripped[1] : raw;
    }
  }
}

/**
 * Interactive full-account sync with live progress (the "Sync all" button).
 * Streams `{progress}` frames over SSE for a Thunderbird-style "folder X of Y —
 * N messages" readout. Plain `useSyncAccount` (JSON) still backs auto-sync.
 * This is an event-driven stream, so it uses local state, not React Query.
 */
export function useSyncAccountStream(): SyncStreamController {
  const queryClient = useQueryClient();
  const [syncing, setSyncing] = useState(false);
  const [progress, setProgress] = useState<SyncProgress | null>(null);
  const [accountScope, setAccountScope] = useState<SyncAccountScope | null>(null);
  const [result, setResult] = useState<{ messagesUpserted: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [failures, setFailures] = useState<SyncFailure[]>([]);

  // Stream one account's sync, pushing progress frames to the live readout.
  // Never throws — transport and `{error}`-frame failures come back as
  // `error` so a multi-account run can record the failure and keep going.
  // (consumeSSEFrames swallows throws from its frame callback, so we can't
  // signal via throw from inside — we capture into a holder and read after.)
  const runOne = useCallback(
    async (accountId: string): Promise<{ upserted: number; error: string | null }> => {
      const box = { upserted: 0, error: null as string | null };
      try {
        const response = await api.streamEmailAccountSync(accountId);
        await consumeSSEFrames(response, (frame) => {
          if (frame.progress) setProgress(frame.progress as SyncProgress);
          else if (frame.error) box.error = String(frame.error);
          else if (frame.done)
            box.upserted = (frame.output as { messagesUpserted: number }).messagesUpserted;
        });
      } catch (e) {
        box.error = e instanceof Error ? e.message : 'Sync failed';
      }
      return box;
    },
    [],
  );

  const start = useCallback(
    async (accountId: string) => {
      setSyncing(true);
      setProgress(null);
      setAccountScope(null);
      setResult(null);
      setError(null);
      setFailures([]);
      try {
        const { upserted, error: err } = await runOne(accountId);
        if (err) setError(friendlySyncError(err));
        else setResult({ messagesUpserted: upserted });
      } finally {
        setSyncing(false);
        setProgress(null);
        queryClient.invalidateQueries({ queryKey: mb.all });
        queryClient.invalidateQueries({ queryKey: conn.all });
      }
    },
    [queryClient, runOne],
  );

  const startAll = useCallback(
    async (accounts: { accountId: string; emailAddress: string }[]) => {
      setSyncing(true);
      setProgress(null);
      setAccountScope(null);
      setResult(null);
      setError(null);
      setFailures([]);
      let total = 0;
      const failed: SyncFailure[] = [];
      try {
        for (let i = 0; i < accounts.length; i++) {
          const acct = accounts[i];
          setAccountScope({
            index: i + 1,
            total: accounts.length,
            emailAddress: acct.emailAddress,
          });
          setProgress(null);
          const { upserted, error: err } = await runOne(acct.accountId);
          total += upserted;
          if (err)
            failed.push({ emailAddress: acct.emailAddress, message: friendlySyncError(err) });
        }
        setResult({ messagesUpserted: total });
        setFailures(failed);
        // Headline: one account names it inline; several defer the per-account
        // reasons to `failures` (the UI lists them under this line).
        if (failed.length === 1) {
          setError(`Couldn't sync ${failed[0].emailAddress}: ${failed[0].message}`);
        } else if (failed.length > 1) {
          setError(`Couldn't sync ${failed.length} of ${accounts.length} accounts:`);
        }
      } finally {
        setSyncing(false);
        setProgress(null);
        setAccountScope(null);
        queryClient.invalidateQueries({ queryKey: mb.all });
        queryClient.invalidateQueries({ queryKey: conn.all });
      }
    },
    [queryClient, runOne],
  );

  return { syncing, progress, accountScope, result, error, failures, start, startAll };
}

export interface BackfillAllController {
  /** True while older-mail passes are streaming. */
  syncing: boolean;
  /** Latest per-folder progress ("INBOX: 120 of 200 · folder 1 of 3"). */
  progress: SyncProgress | null;
  /** Error message if a pass failed. */
  error: string | null;
  /** Stream older-mail to the bottom for one account (single-account "Load all"). */
  start: (accountId: string) => Promise<void>;
  /** Same, across several accounts in sequence (the unified "Load all"). */
  startAll: (accountIds: string[]) => Promise<void>;
}

/**
 * Streamed "Load all messages": walks every folder's OLDER history to the bottom
 * over SSE with live per-message progress (the same feel as "Sync all"). The
 * backend does one bounded pass per request; this repeats passes until it reports
 * `complete`, so a huge mailbox streams smoothly without exhausting memory.
 */
export function useBackfillAllStream(): BackfillAllController {
  const queryClient = useQueryClient();
  const [syncing, setSyncing] = useState(false);
  const [progress, setProgress] = useState<SyncProgress | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Stream passes for one account until every folder reports complete. Returns
  // an error message, or null on success. Never throws (transport + `{error}`
  // frames come back as the return value). The 1000-pass ceiling is a runaway
  // guard — each pass advances the cursor, so it converges long before that.
  const runAccount = useCallback(
    async (accountId: string): Promise<string | null> => {
      let drops = 0;
      for (let pass = 0; pass < 3000; pass++) {
        const box = { complete: false, error: null as string | null };
        try {
          const response = await api.streamEmailAccountSync(accountId, { backfillAll: true });
          await consumeSSEFrames(response, (frame) => {
            if (frame.progress) setProgress(frame.progress as SyncProgress);
            else if (frame.error) box.error = String(frame.error);
            else if (frame.done)
              box.complete = (frame.output as { complete?: boolean })?.complete === true;
          });
        } catch {
          // A dropped/aborted SSE connection is transient: the server advances and
          // PERSISTS each folder's cursor as it streams, so the next pass resumes
          // where this one stopped. Retry a bounded number of consecutive drops
          // rather than aborting the whole walk.
          if (++drops >= 6) {
            return 'The connection kept dropping — some older mail may still be pending. Try “Load all” again.';
          }
          continue;
        }
        if (box.error) return box.error; // a real server-side error (auth, etc.)
        drops = 0;
        // Surface the growing message set between passes.
        queryClient.invalidateQueries({ queryKey: mb.all });
        if (box.complete) return null;
      }
      return null;
    },
    [queryClient],
  );

  const finish = useCallback(() => {
    setSyncing(false);
    setProgress(null);
    queryClient.invalidateQueries({ queryKey: mb.all });
    queryClient.invalidateQueries({ queryKey: conn.all });
  }, [queryClient]);

  const start = useCallback(
    async (accountId: string) => {
      setSyncing(true);
      setProgress(null);
      setError(null);
      try {
        const err = await runAccount(accountId);
        if (err) setError(err);
      } finally {
        finish();
      }
    },
    [runAccount, finish],
  );

  const startAll = useCallback(
    async (accountIds: string[]) => {
      setSyncing(true);
      setProgress(null);
      setError(null);
      const failures: string[] = [];
      try {
        for (const accountId of accountIds) {
          const err = await runAccount(accountId);
          if (err) failures.push(err);
        }
        if (failures.length === 1) setError(failures[0]);
        else if (failures.length > 1)
          setError(`${failures.length} accounts couldn't finish loading`);
      } finally {
        finish();
      }
    },
    [runAccount, finish],
  );

  return { syncing, progress, error, start, startAll };
}

/** Trigger a folder-scoped pull-sync — the folder-open auto-sync path. */
export function useSyncFolder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ accountId, folderId }: { accountId: string; folderId: string }) =>
      api.syncEmailAccount(accountId, folderId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: mb.all });
      queryClient.invalidateQueries({ queryKey: conn.all });
    },
  });
}

/** Permanently empty an account's Trash folder (server + local). Irreversible. */
export function useEmptyTrash() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (accountId: string) => api.emptyEmailTrash(accountId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: mb.all });
      queryClient.invalidateQueries({ queryKey: conn.all });
    },
  });
}

/** Pull one batch of OLDER mail for a folder — the "load older messages" action. */
export function useBackfillFolder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ accountId, folderId }: { accountId: string; folderId: string }) =>
      api.syncEmailAccount(accountId, folderId, true),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: mb.all });
      queryClient.invalidateQueries({ queryKey: conn.all });
    },
  });
}

// ============================================================================
// Optimistic write actions
//
// Server writeback can lag or fail, so these patch the cache immediately and
// reconcile on settle. Each: onMutate cancels in-flight reads, snapshots the
// affected message-list + detail caches, applies the optimistic change;
// onError rolls back from the snapshot; onSettled invalidates to reconcile
// with the eventual server state.
// ============================================================================

interface OptimisticCtx {
  prevLists: [readonly unknown[], EmailMessageDto[] | undefined][];
  prevDetail: EmailMessageDetailDto | undefined;
  messageId: string;
}

/**
 * Apply `fn` to the matching message across every message-list query. Returning
 * `null` from `fn` removes the message from that list (move/delete).
 */
function applyToMessageLists(
  qc: QueryClient,
  messageId: string,
  fn: (m: EmailMessageDto) => EmailMessageDto | null,
): void {
  qc.setQueriesData<EmailMessageDto[]>({ queryKey: mb.messages() }, (old) => {
    if (!old) return old;
    const next: EmailMessageDto[] = [];
    for (const m of old) {
      if (m.messageId !== messageId) {
        next.push(m);
        continue;
      }
      const patched = fn(m);
      if (patched) next.push(patched);
    }
    return next;
  });
}

async function snapshot(qc: QueryClient, messageId: string): Promise<OptimisticCtx> {
  await qc.cancelQueries({ queryKey: mb.all });
  return {
    prevLists: qc.getQueriesData<EmailMessageDto[]>({ queryKey: mb.messages() }),
    prevDetail: qc.getQueryData<EmailMessageDetailDto>(mb.message(messageId)),
    messageId,
  };
}

function rollback(qc: QueryClient, ctx: OptimisticCtx | undefined): void {
  if (!ctx) return;
  for (const [key, data] of ctx.prevLists) qc.setQueryData(key, data);
  qc.setQueryData(mb.message(ctx.messageId), ctx.prevDetail);
}

// While the user triages a FILTERED list, an in-place status change (unstar,
// mark-read) would otherwise make the row vanish on the next reconcile-refetch —
// before they can act on it again. The page suspends reconcile for the duration
// (see `setMailboxReconcileSuspended`), so optimistic patches stay put and the
// row remains visible until the user clicks "Update", which forces a reconcile
// via `useReconcileMailbox`. Module-level (like `writebackFlushTimer` above)
// because there is a single mailbox instance and every write funnels through
// `reconcile`. Optimistic removals (move/delete) still drop their row instantly
// in `onMutate` — suspension only defers the refetch, so a sibling action can't
// prematurely re-drop the rows the user is still working on.
let reconcileSuspended = false;
export function setMailboxReconcileSuspended(suspended: boolean): void {
  reconcileSuspended = suspended;
}

function reconcile(qc: QueryClient): void {
  // Folder counts are LIVE server-side aggregates (listEmailFolders counts rows
  // per folder), so every message write moves them. Refresh them BEFORE the
  // suspension check: suspension exists to keep filtered ROWS on screen during
  // triage, not to freeze the sidebar — leaving it out is why the Inbox count
  // sat still while messages were archived and deleted.
  qc.invalidateQueries({ queryKey: [...mb.all, 'folders'] });
  if (reconcileSuspended) return;
  qc.invalidateQueries({ queryKey: mb.messages() });
  qc.invalidateQueries({ queryKey: mb.threads() });
}

/**
 * Force a reconcile-refetch now, ignoring any active suspension — this is the
 * "Update" affordance that flushes the rows a filtered triage session has left
 * behind (now-non-matching or removed) and re-syncs with the server.
 */
export function useReconcileMailbox(): () => void {
  const qc = useQueryClient();
  return useCallback(() => {
    qc.invalidateQueries({ queryKey: [...mb.all, 'folders'] });
    qc.invalidateQueries({ queryKey: mb.messages() });
    qc.invalidateQueries({ queryKey: mb.threads() });
  }, [qc]);
}

// Debounced holder-level writeback flush. Write actions (read/star/move/delete)
// patch locally + enqueue a server writeback; this pushes those to the server
// within a couple seconds instead of waiting for the next sync, coalescing a
// burst of actions into one IMAP round-trip. Best-effort — the next sync drains
// anything that fails here. Event-driven timer (not React Query state).
let writebackFlushTimer: ReturnType<typeof setTimeout> | null = null;
function scheduleWritebackFlush(): void {
  if (writebackFlushTimer) clearTimeout(writebackFlushTimer);
  writebackFlushTimer = setTimeout(() => {
    writebackFlushTimer = null;
    // Promise.resolve(...) keeps this safe even if a caller/mock returns non-promise.
    void Promise.resolve(api.flushEmailWritebacks()).catch(() => undefined);
  }, 2000);
}

/** Mark a message read/unread (optimistic). */
export function useMarkRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ messageId, value }: { messageId: string; value: boolean }) =>
      api.markEmailMailboxMessageRead(messageId, value),
    onMutate: async ({ messageId, value }) => {
      const ctx = await snapshot(qc, messageId);
      applyToMessageLists(qc, messageId, (m) => ({ ...m, isRead: value }));
      qc.setQueryData<EmailMessageDetailDto>(mb.message(messageId), (old) =>
        old ? { ...old, isRead: value } : old,
      );
      return ctx;
    },
    onError: (_e, _v, ctx) => rollback(qc, ctx),
    onSettled: () => {
      reconcile(qc);
      scheduleWritebackFlush();
    },
  });
}

/** Star/unstar a message (optimistic). */
export function useStarMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ messageId, value }: { messageId: string; value: boolean }) =>
      api.starEmailMailboxMessage(messageId, value),
    onMutate: async ({ messageId, value }) => {
      const ctx = await snapshot(qc, messageId);
      applyToMessageLists(qc, messageId, (m) => ({ ...m, isStarred: value }));
      qc.setQueryData<EmailMessageDetailDto>(mb.message(messageId), (old) =>
        old ? { ...old, isStarred: value } : old,
      );
      return ctx;
    },
    onError: (_e, _v, ctx) => rollback(qc, ctx),
    onSettled: () => {
      reconcile(qc);
      scheduleWritebackFlush();
    },
  });
}

/** Move a message to another folder (optimistic: drops it from the current list). */
export function useMoveMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ messageId, targetFolderId }: { messageId: string; targetFolderId: string }) =>
      api.moveEmailMailboxMessage(messageId, targetFolderId),
    onMutate: async ({ messageId }) => {
      const ctx = await snapshot(qc, messageId);
      applyToMessageLists(qc, messageId, () => null);
      return ctx;
    },
    onError: (_e, _v, ctx) => rollback(qc, ctx),
    onSettled: () => {
      reconcile(qc);
      scheduleWritebackFlush();
    },
  });
}

/**
 * Copy a message into another folder of the SAME account (IMAP COPY, applied
 * server-side in the request). The original stays put — no optimistic list
 * change — and on success the target folder is synced so the copy is ingested
 * locally. `accountId` is only used to target that sync.
 */
export function useCopyMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      messageId,
      targetFolderId,
    }: {
      messageId: string;
      targetFolderId: string;
      accountId: string;
    }) => api.copyEmailMailboxMessage(messageId, targetFolderId),
    onSuccess: (_data, { accountId, targetFolderId }) => {
      void Promise.resolve(api.syncEmailAccount(accountId, targetFolderId))
        .catch(() => undefined)
        .finally(() => reconcile(qc));
    },
    onSettled: () => reconcile(qc),
  });
}

/**
 * Move or copy a message into a folder of a DIFFERENT account. `move` optimistically
 * drops it from the current list (the original heads to its source Trash server-side);
 * `copy` leaves it in place. `scheduleWritebackFlush` drains the source-delete
 * writeback the server enqueued for a move.
 */
export function useMoveMessageToAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      messageId,
      targetAccountId,
      targetFolderId,
      mode,
    }: {
      messageId: string;
      targetAccountId: string;
      targetFolderId: string;
      mode: 'move' | 'copy';
    }) =>
      api.moveEmailMailboxMessageToAccount(messageId, { targetAccountId, targetFolderId, mode }),
    onMutate: async ({ messageId, mode }) => {
      // Copy keeps the source row; only a move removes it from the current list.
      if (mode !== 'move') return undefined;
      const ctx = await snapshot(qc, messageId);
      applyToMessageLists(qc, messageId, () => null);
      return ctx;
    },
    onError: (_e, _v, ctx) => rollback(qc, ctx),
    onSuccess: (_data, { targetAccountId, targetFolderId }) => {
      // The copy is on the target server but not yet in the local Vault — pull
      // the target folder so it appears promptly (best-effort; that folder also
      // auto-syncs when next opened), then refresh the lists once it lands.
      void Promise.resolve(api.syncEmailAccount(targetAccountId, targetFolderId))
        .catch(() => undefined)
        .finally(() => reconcile(qc));
    },
    onSettled: () => {
      reconcile(qc);
      scheduleWritebackFlush();
    },
  });
}

/** Soft-delete a message (optimistic: drops it from the current list). */
export function useDeleteMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (messageId: string) => api.deleteEmailMailboxMessage(messageId),
    onMutate: async (messageId) => {
      const ctx = await snapshot(qc, messageId);
      applyToMessageLists(qc, messageId, () => null);
      return ctx;
    },
    onError: (_e, _v, ctx) => rollback(qc, ctx),
    onSettled: () => {
      reconcile(qc);
      scheduleWritebackFlush();
    },
  });
}

// ============================================================================
// Bulk optimistic write actions (apply one action to a SET of messages)
// ============================================================================

interface BulkCtx {
  prevLists: [readonly unknown[], EmailMessageDto[] | undefined][];
}

async function snapshotBulk(qc: QueryClient): Promise<BulkCtx> {
  await qc.cancelQueries({ queryKey: mb.all });
  return { prevLists: qc.getQueriesData<EmailMessageDto[]>({ queryKey: mb.messages() }) };
}

function rollbackBulk(qc: QueryClient, ctx: BulkCtx | undefined): void {
  if (!ctx) return;
  for (const [key, data] of ctx.prevLists) qc.setQueryData(key, data);
}

/** Apply `fn` to every message in `ids` across all lists (null removes it). */
function applyToMessageListsBulk(
  qc: QueryClient,
  ids: Set<string>,
  fn: (m: EmailMessageDto) => EmailMessageDto | null,
): void {
  qc.setQueriesData<EmailMessageDto[]>({ queryKey: mb.messages() }, (old) => {
    if (!old) return old;
    const next: EmailMessageDto[] = [];
    for (const m of old) {
      if (!ids.has(m.messageId)) {
        next.push(m);
        continue;
      }
      const patched = fn(m);
      if (patched) next.push(patched);
    }
    return next;
  });
}

/** Bulk mark read/unread (optimistic). */
export function useBulkMarkRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ messageIds, value }: { messageIds: string[]; value: boolean }) =>
      api.bulkMarkEmailMailboxRead(messageIds, value),
    onMutate: async ({ messageIds, value }) => {
      const ctx = await snapshotBulk(qc);
      applyToMessageListsBulk(qc, new Set(messageIds), (m) => ({ ...m, isRead: value }));
      return ctx;
    },
    onError: (_e, _v, ctx) => rollbackBulk(qc, ctx),
    onSettled: () => {
      reconcile(qc);
      scheduleWritebackFlush();
    },
  });
}

/** Bulk star/unstar (optimistic). */
export function useBulkStar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ messageIds, value }: { messageIds: string[]; value: boolean }) =>
      api.bulkStarEmailMailbox(messageIds, value),
    onMutate: async ({ messageIds, value }) => {
      const ctx = await snapshotBulk(qc);
      applyToMessageListsBulk(qc, new Set(messageIds), (m) => ({ ...m, isStarred: value }));
      return ctx;
    },
    onError: (_e, _v, ctx) => rollbackBulk(qc, ctx),
    onSettled: () => {
      reconcile(qc);
      scheduleWritebackFlush();
    },
  });
}

/** Bulk move to a folder or system role (optimistic: drops from current lists). */
export function useBulkMove() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      messageIds,
      target,
    }: {
      messageIds: string[];
      target: Parameters<typeof api.bulkMoveEmailMailbox>[1];
    }) => api.bulkMoveEmailMailbox(messageIds, target),
    onMutate: async ({ messageIds }) => {
      const ctx = await snapshotBulk(qc);
      applyToMessageListsBulk(qc, new Set(messageIds), () => null);
      return ctx;
    },
    onError: (_e, _v, ctx) => rollbackBulk(qc, ctx),
    onSettled: () => {
      reconcile(qc);
      scheduleWritebackFlush();
    },
  });
}

/** Bulk soft-delete (optimistic: drops from current lists). */
export function useBulkDelete() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (messageIds: string[]) => api.bulkDeleteEmailMailbox(messageIds),
    onMutate: async (messageIds) => {
      const ctx = await snapshotBulk(qc);
      applyToMessageListsBulk(qc, new Set(messageIds), () => null);
      return ctx;
    },
    onError: (_e, _v, ctx) => rollbackBulk(qc, ctx),
    onSettled: () => {
      reconcile(qc);
      scheduleWritebackFlush();
    },
  });
}

// Bulk-by-filter ("select all in folder") acts on the whole matching set
// server-side — the affected ids aren't known on the client, so these can't do
// the id-based optimistic patch the bulk hooks above use. Instead they refetch
// the message lists + folder counts once the server has applied the change.
function invalidateMailboxAfterBulk(qc: QueryClient) {
  qc.invalidateQueries({ queryKey: mb.messages() });
  qc.invalidateQueries({ queryKey: mb.threads() });
  qc.invalidateQueries({ queryKey: [...mb.all, 'folders'] });
}

/** Move EVERY message matching a folder filter (archive / delete / move all). */
export function useBulkMoveByFilter() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      scope,
      target,
    }: {
      scope: EmailBulkFilterScope;
      target: Parameters<typeof api.bulkMoveByFilterEmailMailbox>[1];
    }) => api.bulkMoveByFilterEmailMailbox(scope, target),
    onSettled: () => {
      invalidateMailboxAfterBulk(qc);
      scheduleWritebackFlush();
    },
  });
}

/** Mark read/unread or star/unstar EVERY message matching a folder filter. */
export function useBulkFlagByFilter() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      scope,
      field,
      value,
    }: {
      scope: EmailBulkFilterScope;
      field: 'read' | 'star';
      value: boolean;
    }) => api.bulkFlagByFilterEmailMailbox(scope, field, value),
    onSettled: () => {
      invalidateMailboxAfterBulk(qc);
      scheduleWritebackFlush();
    },
  });
}

// Custom-folder management (server-backed over IMAP). Each refreshes the folder
// list on success and surfaces the server's typed error message on failure.
const folderErr = (e: unknown) =>
  e instanceof Error && e.message ? e.message : 'Folder operation failed';
const invalidateFolders = (qc: QueryClient) =>
  qc.invalidateQueries({ queryKey: [...mb.all, 'folders'] });

/** Create a custom folder (optionally nested under `parentFolderId`). */
export function useCreateFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      accountId,
      name,
      parentFolderId,
    }: {
      accountId: string;
      name: string;
      parentFolderId?: string;
    }) => api.createEmailMailboxFolder(accountId, name, parentFolderId),
    onSuccess: () => {
      toast({ description: 'Folder created' });
      invalidateFolders(qc);
    },
    onError: (e) => toast({ description: folderErr(e), variant: 'destructive' }),
  });
}

/** Rename a custom folder. */
export function useRenameFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      folderId,
      accountId,
      name,
    }: {
      folderId: string;
      accountId: string;
      name: string;
    }) => api.renameEmailMailboxFolder(folderId, accountId, name),
    onSuccess: () => {
      toast({ description: 'Folder renamed' });
      invalidateFolders(qc);
    },
    onError: (e) => toast({ description: folderErr(e), variant: 'destructive' }),
  });
}

/** Delete a custom folder. */
export function useDeleteFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ folderId, accountId }: { folderId: string; accountId: string }) =>
      api.deleteEmailMailboxFolder(folderId, accountId),
    onSuccess: () => {
      toast({ description: 'Folder deleted' });
      invalidateFolders(qc);
    },
    onError: (e) => toast({ description: folderErr(e), variant: 'destructive' }),
  });
}

/** Compose + send a message; refreshes the mailbox (the Sent copy appears). */
export function useSendEmail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Parameters<typeof api.sendEmailMailboxMessage>[0]) =>
      api.sendEmailMailboxMessage(input),
    onSuccess: () => {
      // Tied to the send succeeding (not the dialog close), and the toaster is
      // mounted app-level so it survives the compose dialog unmounting.
      toast({ description: 'Message sent' });
      qc.invalidateQueries({ queryKey: mb.messages() });
      qc.invalidateQueries({ queryKey: mb.threads() });
    },
  });
}

/** Queue a message to send at a future time. */
export function useScheduleSend() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Parameters<typeof api.scheduleEmailMailboxMessage>[0]) =>
      api.scheduleEmailMailboxMessage(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: mb.scheduled() }),
  });
}

/** Set per-account operational-folder overrides (Sent/Drafts/Trash/Spam/Archive). */
export function useSetFolderRoleOverrides() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      accountId: string;
      overrides: Parameters<typeof api.setEmailFolderRoleOverrides>[1];
    }) => api.setEmailFolderRoleOverrides(vars.accountId, vars.overrides),
    onSuccess: () => {
      toast({ description: 'Folder preferences saved' });
      qc.invalidateQueries({ queryKey: queryKeys.email.connections.all });
      qc.invalidateQueries({ queryKey: [...mb.all, 'folders'] });
    },
  });
}

/** Save the composition to the Drafts folder (supersedes a resumed draft). */
export function useSaveDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Parameters<typeof api.saveEmailMailboxDraft>[0]) =>
      api.saveEmailMailboxDraft(input),
    onSuccess: () => {
      toast({ description: 'Draft saved' });
      // Refreshes the Drafts folder (and any thread aggregates the draft touched).
      qc.invalidateQueries({ queryKey: mb.messages() });
      qc.invalidateQueries({ queryKey: mb.threads() });
      // Prefix-match every account's folder list so the Drafts count updates.
      qc.invalidateQueries({ queryKey: [...mb.all, 'folders'] });
    },
  });
}

/**
 * Discard a draft (optimistic: drops it from the Drafts list immediately). Uses
 * the dedicated hard-delete endpoint rather than a move-to-Trash, so it never
 * lingers when the account has no Trash folder and can't reappear on re-sync.
 */
export function useDiscardDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (messageId: string) => api.discardEmailMailboxDraft(messageId),
    onMutate: async (messageId) => {
      const ctx = await snapshot(qc, messageId);
      applyToMessageLists(qc, messageId, () => null);
      return ctx;
    },
    onError: (_e, _v, ctx) => rollback(qc, ctx),
    onSettled: () => {
      reconcile(qc);
      // The Drafts folder count drops — refresh every account's folder list.
      qc.invalidateQueries({ queryKey: [...mb.all, 'folders'] });
    },
  });
}

/** The user's pending scheduled sends. */
export function useScheduledSends(enabled = true) {
  return useQuery({
    queryKey: mb.scheduled(),
    queryFn: async () => (await api.listEmailScheduledSends()).scheduled,
    enabled,
  });
}

/** Cancel a pending scheduled send. */
export function useCancelScheduledSend() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (actionId: string) => api.cancelEmailScheduledSend(actionId),
    onSuccess: () => qc.invalidateQueries({ queryKey: mb.scheduled() }),
  });
}

/** Snooze a message until a wake time (optimistic: drops it from the inbox list). */
export function useSnoozeMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ messageId, snoozeUntil }: { messageId: string; snoozeUntil: string }) =>
      api.snoozeEmailMailboxMessage(messageId, snoozeUntil),
    onMutate: async ({ messageId }) => {
      const ctx = await snapshot(qc, messageId);
      applyToMessageLists(qc, messageId, () => null);
      return ctx;
    },
    onError: (_e, _v, ctx) => rollback(qc, ctx),
    onSettled: () => {
      reconcile(qc);
      qc.invalidateQueries({ queryKey: mb.snoozed() });
    },
  });
}

/** Snoozed messages for an account (the Snoozed view). */
export function useSnoozedMessages(accountId: string | undefined, enabled = true) {
  return useQuery<EmailMessageDto[]>({
    queryKey: mb.snoozedList(accountId ?? ''),
    queryFn: async () =>
      (await api.listEmailMailboxMessages(accountId as string, { snoozed: 'only' })).messages,
    enabled: enabled && !!accountId,
  });
}

/** Resurface a snoozed message now. */
export function useUnsnoozeMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (messageId: string) => api.unsnoozeEmailMailboxMessage(messageId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: mb.snoozed() });
      reconcile(qc);
    },
  });
}
